/**
 * TokenStore — single source of truth for JWT auth token persistence.
 *
 * Why this exists (P1-7 audit, 17 Apr 2026):
 *   The JWT was read/written via `localStorage.getItem('token')` in ~30+ places
 *   across the SPA (router guards, axios interceptor, Vuex modules, Vue
 *   components). Centralizing access here is a pure refactor: it does NOT
 *   change the security posture today, but it gives us a single seam to swap
 *   the storage backend later (HttpOnly cookie, sessionStorage, in-memory)
 *   without touching dozens of callsites.
 *
 * Tokens managed:
 *   - `token`     : active JWT used by axios `Authorization` header.
 *   - `mainToken` : super-admin's own JWT, parked here while impersonating
 *                   another user via the "login-as-user" flow. Restored on
 *                   "logoutAsUser".
 *
 * Behavior preserved 1:1 with the previous direct localStorage usage:
 *   - `setToken(null|undefined)` writes the empty string (some legacy paths
 *     used `localStorage.setItem('token', '')` instead of `removeItem`).
 *   - `clearToken()` calls `removeItem` (used by 401 interceptor + logout).
 *   - `hasValidToken()` mirrors the long boolean chain used in router guards
 *     to defend against the literal strings 'undefined' / 'null'.
 */

const TOKEN_KEY = 'token';
const MAIN_TOKEN_KEY = 'mainToken';

function isUsable(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    value !== 'null' &&
    value !== 'undefined'
  );
}

export const TokenStore = {
  // --- primary token ---
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },
  setToken(token) {
    // Preserve legacy semantics: callers sometimes pass '' to clear softly.
    localStorage.setItem(TOKEN_KEY, token == null ? '' : token);
  },
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
  },
  hasToken() {
    return !!this.getToken();
  },
  hasValidToken() {
    return isUsable(this.getToken());
  },

  // --- impersonation main token (super-admin "login-as-user" flow) ---
  getMainToken() {
    return localStorage.getItem(MAIN_TOKEN_KEY);
  },
  setMainToken(token) {
    localStorage.setItem(MAIN_TOKEN_KEY, token == null ? '' : token);
  },
  clearMainToken() {
    // Legacy code uses setItem('mainToken', '') — keep that exact byte pattern
    // so any code reading the raw value still sees '' rather than null.
    localStorage.setItem(MAIN_TOKEN_KEY, '');
  },
  hasMainToken() {
    return isUsable(this.getMainToken());
  },
};

export default TokenStore;
